import {
  Actor,
  Body,
  Collider,
  Falling,
  Launch,
  Static,
  Transform,
  Velocity,
} from '@/components'
import type { Entity } from '@/core/ecs/Entity'
import type { SystemContext } from '@/core/ecs/Scheduler'
import type { World } from '@/core/ecs/World'
import { BUMP_INSTABILITY } from '@/game/abilities/definitions'
import { applyKnockback } from '@/game/abilities/handlers'

const ITERATIONS = 3
/** Relative closing speed above which a bump counts as a hit, m/s. */
const BUMP_SPEED = 5
const SLOP = 0.001

/**
 * Hard ceiling on any impulse a collision may hand out, m/s.
 *
 * Measured before the rewrite, a body-to-body ram delivered up to 35.65 m/s —
 * more than the strongest ability in the game, from an action nobody chose to
 * perform. Capping it below the launch threshold makes collisions incapable of
 * throwing anyone: every launch now has an author. See docs/PHYSICS.md §6.5.
 */
const BUMP_MAX = 8
/** The same ceiling for a prop shoved into someone; deliberately below Push. */
const PROP_MAX = 12

/**
 * Impulse-based resolution for circles and static boxes.
 *
 * Brute force over every pair is deliberate: an arena holds well under a
 * hundred bodies, so a broadphase would cost more in complexity than it saves
 * in microseconds. Three relaxation passes are enough to settle a character
 * wedged between a crate and a car without visible jitter.
 */
export function collisionSystem({ world, dt }: SystemContext): void {
  const dynamics = world
    .query([Transform, Velocity, Collider, Body], { none: [Static, Falling] })
    .entities()
  const statics = world.query([Transform, Collider, Body, Static]).entities()

  for (let iter = 0; iter < ITERATIONS; iter++) {
    const report = iter === 0

    for (let i = 0; i < dynamics.length; i++) {
      const a = dynamics[i]
      for (let j = i + 1; j < dynamics.length; j++) {
        resolveDynamicPair(world, a, dynamics[j], report)
      }
      for (const s of statics) {
        resolveStatic(world, a, s, iter === 0, dt)
      }
    }
  }
}

function resolveDynamicPair(world: World, a: Entity, b: Entity, report: boolean): void {
  const ta = world.read(a, Transform)
  const tb = world.read(b, Transform)
  const ca = world.read(a, Collider)
  const cb = world.read(b, Collider)

  let dx = tb.x - ta.x
  let dz = tb.z - ta.z
  let dist = Math.hypot(dx, dz)
  const minDist = ca.radius + cb.radius
  if (dist >= minDist) return

  if (dist < 1e-5) {
    // Perfectly co-located bodies need an arbitrary but stable separation axis.
    dx = 1
    dz = 0
    dist = 1e-5
  } else {
    dx /= dist
    dz /= dist
  }

  const ba = world.read(a, Body)
  const bb = world.read(b, Body)
  const invSum = ba.invMass + bb.invMass
  if (invSum <= 0) return

  const penetration = minDist - dist
  const correction = Math.max(penetration - SLOP, 0)
  ta.x -= dx * correction * (ba.invMass / invSum)
  ta.z -= dz * correction * (ba.invMass / invSum)
  tb.x += dx * correction * (bb.invMass / invSum)
  tb.z += dz * correction * (bb.invMass / invSum)

  const va = world.read(a, Velocity)
  const vb = world.read(b, Velocity)
  const relative = (vb.x - va.x) * dx + (vb.z - va.z) * dz
  if (relative > 0) return // already separating

  const restitution = Math.max(ba.restitution, bb.restitution)
  const impulse = (-(1 + restitution) * relative) / invSum

  // A prop striking a fighter has to go through the same door as everything
  // else. It used to write straight into the velocity here, which meant a
  // pushed crate — 30 m/s off a single Push — hit for more than any ability
  // while ignoring Brace, dash armour, the shield pickup and instability
  // scaling. It was, by accident, the strongest attack in the game.
  const aActor = world.has(a, Actor)
  const bActor = world.has(b, Actor)
  const propHit = aActor !== bActor

  if (propHit) {
    const actor = aActor ? a : b
    const prop = aActor ? b : a
    const sign = aActor ? -1 : 1
    const actorBody = aActor ? ba : bb
    const propBody = aActor ? bb : ba
    const actorVel = aActor ? va : vb
    const propVel = aActor ? vb : va

    propVel.x -= dx * impulse * propBody.invMass * sign
    propVel.z -= dz * impulse * propBody.invMass * sign

    const power = Math.min(PROP_MAX, impulse * actorBody.invMass)
    if (power > 0.2) {
      applyKnockback(
        world,
        actor,
        dx * sign,
        dz * sign,
        power,
        BUMP_INSTABILITY,
        'bump',
        prop,
        ta.x + dx * ca.radius,
        ta.z + dz * ca.radius,
      )
    } else {
      actorVel.x += dx * power * sign
      actorVel.z += dz * power * sign
    }
    return
  }

  const closing = -relative
  const hard = report && closing >= BUMP_SPEED
  const hx = ta.x + dx * ca.radius
  const hz = ta.z + dz * ca.radius

  for (const [target, sign, other, body] of [
    [a, -1, b, ba],
    [b, 1, a, bb],
  ] as Array<[Entity, number, Entity, { invMass: number }]>) {
    // A fighter in flight is on an authored arc. Letting a collision bend it
    // would put the outcome back in the hands of whoever happened to be
    // standing there — they still get separated, they just don't get shoved.
    if (world.has(target, Launch)) continue

    const power = Math.min(BUMP_MAX, impulse * body.invMass)

    // A hard shoulder-charge goes through the same door as every ability, so
    // Brace, the shield pickup and dash armour all answer it. A gentle contact
    // is just separation and stays out of the event stream entirely.
    if (hard) {
      applyKnockback(world, target, dx * sign, dz * sign, power, BUMP_INSTABILITY, 'bump', other, hx, hz)
    } else {
      const vel = world.read(target, Velocity)
      vel.x += dx * power * sign
      vel.z += dz * power * sign
    }
  }
}

function resolveStatic(
  world: World,
  dyn: Entity,
  solid: Entity,
  firstIteration: boolean,
  dt: number,
): void {
  const td = world.read(dyn, Transform)
  const cd = world.read(dyn, Collider)
  const ts = world.read(solid, Transform)
  const cs = world.read(solid, Collider)
  const bs = world.read(solid, Body)

  let nx = 0
  let nz = 0
  let penetration = 0

  if (cs.shape === 'circle') {
    let dx = td.x - ts.x
    let dz = td.z - ts.z
    const dist = Math.hypot(dx, dz)
    const minDist = cd.radius + cs.radius
    if (dist >= minDist) return
    if (dist < 1e-5) {
      dx = 1
      dz = 0
    } else {
      dx /= dist
      dz /= dist
    }
    nx = dx
    nz = dz
    penetration = minDist - dist
  } else {
    // Work in the box's local frame: the closest-point test is trivial there,
    // and the resulting normal rotates back out with the same matrix.
    const cos = Math.cos(cs.angle)
    const sin = Math.sin(cs.angle)
    const rx = td.x - ts.x
    const rz = td.z - ts.z
    const lx = rx * cos + rz * sin
    const lz = -rx * sin + rz * cos

    const clampedX = Math.max(-cs.halfX, Math.min(cs.halfX, lx))
    const clampedZ = Math.max(-cs.halfZ, Math.min(cs.halfZ, lz))
    let dx = lx - clampedX
    let dz = lz - clampedZ
    let dist = Math.hypot(dx, dz)

    if (dist > 1e-5) {
      if (dist >= cd.radius) return
      dx /= dist
      dz /= dist
      penetration = cd.radius - dist
    } else {
      // Centre is inside the box: escape along the shallowest face.
      const overlapX = cs.halfX - Math.abs(lx)
      const overlapZ = cs.halfZ - Math.abs(lz)
      if (overlapX < overlapZ) {
        dx = Math.sign(lx) || 1
        dz = 0
        penetration = overlapX + cd.radius
      } else {
        dx = 0
        dz = Math.sign(lz) || 1
        penetration = overlapZ + cd.radius
      }
      dist = 0
    }

    nx = dx * cos - dz * sin
    nz = dx * sin + dz * cos
  }

  const vd = world.read(dyn, Velocity)

  if (bs.absorb > 0) {
    // Soft barrier: a knee-high guard rail should not be a wall. It never
    // blocks movement, it just bleeds speed off whatever crosses it.
    //
    // Applied once per step and scaled by dt. It used to run on all three
    // relaxation iterations with a flat 20 % each, i.e. 0.8³ = 0.51 of your
    // speed *per frame* — which made every guard rail an unclimbable wall and
    // quietly sealed two arena rims.
    if (!firstIteration) return
    const keep = Math.exp(-bs.absorb * 9 * dt)
    vd.x *= keep
    vd.z *= keep
    return
  }

  td.x += nx * penetration
  td.z += nz * penetration

  const along = vd.x * nx + vd.z * nz
  if (along >= 0) return
  const bounce = 1 + bs.restitution
  vd.x -= nx * along * bounce
  vd.z -= nz * along * bounce
}
